import React from 'react';
import { Lock, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * OverlayGate - Gating component for premium features
 * @param {boolean} locked - Whether the content is locked
 * @param {string} title - Title of the gate
 * @param {string} message - Description message
 * @param {string} primaryCta - Text for the primary action button
 * @param {string} secondaryCta - Text for the secondary action button
 * @param {function} onPrimary - Action for the primary button
 * @param {function} onSecondary - Action for the secondary button
 * @param {React.ReactNode} children - The content to be gated
 * @param {string} className - Additional CSS classes
 */
const OverlayGate = ({
    locked,
    title = 'Contenido No Disponible',
    message = 'Esta herramienta está reservada para planes profesionales.',
    primaryCta = 'Actualizar Plan',
    secondaryCta = 'Ver Planes',
    onPrimary,
    onSecondary,
    children,
    className = ''
}) => {
    return (
        <div className={`relative w-full h-full min-h-[200px] ${className}`}>
            {/* Content properly blurred and uninteractable when locked */}
            <div className={`w-full h-full transition-all duration-300 ${locked ? 'blur-md grayscale-[0.5] pointer-events-none select-none opacity-40' : ''}`}>
                {children}
            </div>

            {/* Premium Overlay */}
            {locked && (
                <div
                    className="absolute inset-0 z-[50] flex items-start justify-center p-6 text-center animate-in fade-in zoom-in duration-300 rounded-2xl overflow-y-auto"
                    style={{ background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(8px)' }}
                >
                    <div className="max-w-md w-full bg-white/10 border border-white/20 backdrop-blur-xl rounded-3xl p-8 shadow-2xl mt-20 relative">
                        <div className="mx-auto w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-6 border border-primary/30">
                            <Lock className="w-8 h-8 text-white" />
                        </div>

                        <h3 className="text-2xl font-bold text-white mb-3">
                            {title}
                        </h3>

                        <p className="text-white/80 mb-8 leading-relaxed text-sm">
                            {message}
                        </p>

                        <div className="flex flex-col gap-3">
                            <Button
                                onClick={onPrimary}
                                className="w-full bg-[#22C55E] hover:bg-[#1ea850] text-white font-bold h-12 text-base shadow-lg transition-transform active:scale-95"
                            >
                                <Rocket className="w-4 h-4 mr-2" />
                                {primaryCta}
                            </Button>

                            {secondaryCta && (
                                <Button
                                    variant="outline"
                                    className="w-full border-white/30 text-white hover:bg-white/10 h-12"
                                    onClick={onSecondary}
                                >
                                    {secondaryCta}
                                </Button>
                            )}
                        </div>

                        <p className="mt-6 text-[10px] text-white/40 uppercase tracking-widest font-medium">
                            Acceso Instantáneo tras Activar
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OverlayGate;
